import { env } from "#/env";

/**
 * Analytics engine for the landing page.
 *
 * One `emit()` funnel feeds both destinations:
 *   - Meta Pixel  — standard events go through `fbq('track', …)`, everything
 *     else through `fbq('trackCustom', …)`. Every call carries an `eventID`
 *     so a Conversions API relay can be added later without double counting.
 *   - GA4         — same event under a snake_case name via `gtag('event', …)`.
 *
 * Nothing here ever sends personally identifiable data (name / phone / email).
 * Only the shape of the visit travels: what was seen, clicked, filled, and how
 * the visit ended.
 */

declare global {
	interface Window {
		fbq?: (...args: Array<unknown>) => void;
		gtag?: (...args: Array<unknown>) => void;
		dataLayer?: Array<unknown>;
	}
}

type ParamValue = string | number | boolean | null | undefined;
export type Params = Record<string, ParamValue>;

const GA_ID = env.VITE_GA_MEASUREMENT_ID;
const DEBUG = env.VITE_ANALYTICS_DEBUG === "true";
const ADVANCED_MATCHING = env.VITE_META_ADVANCED_MATCHING === "true";
const LEAD_VALUE = env.VITE_LEAD_VALUE;

/**
 * Each training has its own Meta Pixel (set from the DB, see Training.metaPixelId).
 * The training route calls setMetaPixelId() once its pixel is initialized; every
 * `fbq(...)` call below reports against whatever pixel that route already `init`'d.
 */
let metaPixelId: string | null = null;
export function setMetaPixelId(id: string | null) {
	metaPixelId = id;
	// biome-ignore lint/suspicious/noConsole: no-console is fine for debug logging
	if (id) console.log("[Meta Pixel] active pixel for this page:", id);
	// biome-ignore lint/suspicious/noConsole: no-console is fine for debug logging
	else console.log("[Meta Pixel] no pixel active for this page");
}

/**
 * Meta's fixed vocabulary. Anything outside this list must be sent with
 * `trackCustom`, otherwise the pixel silently drops it.
 */
const META_STANDARD = new Set([
	"AddPaymentInfo",
	"AddToCart",
	"AddToWishlist",
	"CompleteRegistration",
	"Contact",
	"CustomizeProduct",
	"Donate",
	"FindLocation",
	"InitiateCheckout",
	"Lead",
	"PageView",
	"Purchase",
	"Schedule",
	"Search",
	"StartTrial",
	"SubmitApplication",
	"Subscribe",
	"ViewContent",
]);

/* -------------------------------------------------------------------------- */
/* session state                                                              */
/* -------------------------------------------------------------------------- */

export interface FormState {
	id: string;
	started: boolean;
	startedAt: number;
	/** Fields that were focused, in order. */
	touched: Array<string>;
	/** Fields that ended up with a value. */
	filled: Array<string>;
	lastField: string;
	errors: number;
	attempts: number;
	submitted: boolean;
}

interface SessionState {
	startedAt: number;
	path: string;
	locale: string;
	/** Deepest scroll reached on this page view, in percent. */
	maxScroll: number;
	scrollMarks: Set<number>;
	sections: Array<string>;
	deepestSection: string;
	interactions: number;
	clicks: number;
	ctaClicks: number;
	firstInteractionAt: number | null;
	/** Milliseconds where the tab was visible *and* the visitor was active. */
	activeMs: number;
	lastActivityAt: number;
	hiddenCount: number;
	lead: boolean;
	leaveSent: boolean;
	form: FormState | null;
	once: Set<string>;
}

const now = () => (typeof performance !== "undefined" ? performance.now() : 0);

export const session: SessionState = {
	startedAt: now(),
	path: "",
	locale: "ar",
	maxScroll: 0,
	scrollMarks: new Set(),
	sections: [],
	deepestSection: "",
	interactions: 0,
	clicks: 0,
	ctaClicks: 0,
	firstInteractionAt: null,
	activeMs: 0,
	lastActivityAt: now(),
	hiddenCount: 0,
	lead: false,
	leaveSent: false,
	form: null,
	once: new Set(),
};

/** Seconds since this page view started. */
export const secondsOnPage = () =>
	Math.round((now() - session.startedAt) / 100) / 10;
/** Seconds the visitor was actually looking at (and using) the page. */
export const engagedSeconds = () => Math.round(session.activeMs / 100) / 10;

/** Fires the callback only the first time this key is seen on this page view. */
export function once(key: string, run: () => void) {
	if (session.once.has(key)) return;
	session.once.add(key);
	run();
}

/** Called by the tracker on every real interaction — feeds engaged-time. */
export function markInteraction(kind?: string) {
	const at = now();
	// A single scroll gesture fires dozens of events: they all refresh the
	// activity clock, but only one a second counts, so `interactions` stays a
	// meaningful number rather than a scroll-event tally.
	if (
		session.firstInteractionAt === null ||
		at - session.lastActivityAt > 1000
	) {
		session.interactions += 1;
	}
	session.lastActivityAt = at;
	if (session.firstInteractionAt === null) {
		session.firstInteractionAt = at;
		emit("first_interaction", {
			meta: "FirstInteraction",
			params: { interaction_type: kind ?? "unknown" },
		});
	}
}

/** Resets the per-page counters on a client-side route change. */
export function resetPageMetrics(path: string) {
	session.startedAt = now();
	session.path = path;
	session.maxScroll = 0;
	session.scrollMarks = new Set();
	session.sections = [];
	session.deepestSection = "";
	session.clicks = 0;
	session.ctaClicks = 0;
	session.activeMs = 0;
	session.lastActivityAt = now();
	session.hiddenCount = 0;
	session.leaveSent = false;
	session.once = new Set();
}

export function setLocaleContext(locale: string) {
	session.locale = locale;
}

/* -------------------------------------------------------------------------- */
/* attribution                                                                */
/* -------------------------------------------------------------------------- */

export interface Attribution {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_content?: string;
	utm_term?: string;
	/** Meta / Google / TikTok click ids — useful for offline conversion upload. */
	fbclid?: string;
	gclid?: string;
	ttclid?: string;
	referrer?: string;
	landing_path?: string;
}

const ATTR_KEY = "ma_attribution";
let attribution: Attribution | null = null;

/**
 * First-touch attribution: parsed from the very first URL of the session and
 * kept in sessionStorage, so a visitor who navigates around still reports the
 * ad that brought them in.
 */
export function getAttribution(): Attribution {
	if (attribution) return attribution;
	if (typeof window === "undefined") return {};

	try {
		const stored = window.sessionStorage.getItem(ATTR_KEY);
		if (stored) {
			attribution = JSON.parse(stored) as Attribution;
			return attribution;
		}
	} catch {
		// private mode / storage disabled — fall through to a fresh parse
	}

	const url = new URL(window.location.href);
	const q = (key: string) => url.searchParams.get(key) ?? undefined;
	const referrer = document.referrer
		? new URL(document.referrer).hostname
		: "direct";

	const parsed: Attribution = {
		utm_source: q("utm_source"),
		utm_medium: q("utm_medium"),
		utm_campaign: q("utm_campaign"),
		utm_content: q("utm_content"),
		utm_term: q("utm_term"),
		fbclid: q("fbclid"),
		gclid: q("gclid"),
		ttclid: q("ttclid"),
		referrer,
		landing_path: url.pathname,
	};

	// Meta ads that land without UTMs still carry fbclid — treat it as a source
	// so paid traffic never shows up as "direct" in the lead table.
	if (!parsed.utm_source && parsed.fbclid) {
		parsed.utm_source = "facebook";
		parsed.utm_medium = parsed.utm_medium ?? "paid_social";
	}
	if (!parsed.utm_source && parsed.gclid) {
		parsed.utm_source = "google";
		parsed.utm_medium = parsed.utm_medium ?? "cpc";
	}

	attribution = parsed;
	try {
		window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(parsed));
	} catch {
		// non-fatal — attribution just won't survive a reload
	}
	return parsed;
}

/** The subset of attribution stored on the Lead row. */
export function leadAttribution() {
	const a = getAttribution();
	return {
		utmSource: a.utm_source,
		utmMedium: a.utm_medium,
		utmCampaign: a.utm_campaign,
	};
}

/* -------------------------------------------------------------------------- */
/* device context                                                             */
/* -------------------------------------------------------------------------- */

function deviceContext(): Params {
	if (typeof window === "undefined") return {};
	const coarse =
		typeof window.matchMedia === "function" &&
		window.matchMedia("(pointer: coarse)").matches;
	const width = window.innerWidth;
	const connection = (
		navigator as Navigator & { connection?: { effectiveType?: string } }
	).connection;
	return {
		device: coarse ? "touch" : "pointer",
		viewport: width < 640 ? "sm" : width < 1024 ? "md" : "lg",
		viewport_w: width,
		viewport_h: window.innerHeight,
		connection: connection?.effectiveType,
	};
}

/* -------------------------------------------------------------------------- */
/* dispatch                                                                   */
/* -------------------------------------------------------------------------- */

/** GA4 rejects long values; Meta tolerates them but they're noise. */
function clean(params: Params): Record<string, string | number | boolean> {
	const out: Record<string, string | number | boolean> = {};
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;
		out[key] =
			typeof value === "string"
				? value.slice(0, 100)
				: typeof value === "number"
					? Math.round(value * 100) / 100
					: value;
	}
	return out;
}

let eventSeq = 0;
function makeEventId(name: string) {
	eventSeq += 1;
	return `${name}.${Math.round(Date.now() / 1000)}.${eventSeq}.${Math.random()
		.toString(36)
		.slice(2, 8)}`;
}

/** Context stamped onto every single event. */
function baseParams(): Params {
	const a = getAttribution();
	return {
		page_path: session.path || undefined,
		locale: session.locale,
		seconds_on_page: secondsOnPage(),
		engaged_seconds: engagedSeconds(),
		scroll_depth: session.maxScroll,
		sections_seen: session.sections.length,
		interactions: session.interactions,
		utm_source: a.utm_source,
		utm_medium: a.utm_medium,
		utm_campaign: a.utm_campaign,
		referrer: a.referrer,
	};
}

/**
 * Routes that must never reach the ad pixel: the sales team's back office and
 * the internal hero-design previews. Without this, staff opening the leads
 * table would pollute the audiences and dilute every click-through rate.
 */
const PRIVATE_PATHS = /^\/(admin|hero-[abc]|api)(\/|$)/;

export function isTrackedPath(path: string) {
	return !PRIVATE_PATHS.test(path);
}

interface EmitOptions {
	/** Meta event name — `false` sends to GA4 only. */
	meta?: string | false;
	params?: Params;
	/** Include the heavier device block (session-level events only). */
	withDevice?: boolean;
}

/**
 * The single path every event takes. `name` is the GA4 (snake_case) name;
 * `options.meta` is the Meta name when the event should reach the pixel.
 */
export function emit(name: string, options: EmitOptions = {}) {
	if (typeof window === "undefined") return;
	// Read the live location rather than the tracked one: a client-side
	// navigation into /admin must stop reporting immediately.
	if (!isTrackedPath(window.location.pathname)) return;

	const payload = clean({
		...baseParams(),
		...(options.withDevice ? deviceContext() : {}),
		...options.params,
	});
	const eventId = makeEventId(name);

	if (DEBUG) {
		console.info(`[analytics] ${name}`, { meta: options.meta, ...payload });
	}

	if (typeof window.fbq === "function" && options.meta !== false) {
		const metaName = options.meta ?? toPascal(name);
		const method = META_STANDARD.has(metaName) ? "track" : "trackCustom";
		try {
			window.fbq(method, metaName, payload, { eventID: eventId });
		} catch {
			// never let a blocked pixel break the page
		}
	}

	if (GA_ID && typeof window.gtag === "function") {
		try {
			window.gtag("event", name, { ...payload, event_id: eventId });
		} catch {
			// same — analytics is best-effort
		}
	}
}

function toPascal(snake: string) {
	return snake
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join("");
}

/**
 * Meta Advanced Matching — re-inits the pixel with the lead's contact details
 * so Meta can attribute the conversion to the person who saw the ad. The pixel
 * hashes the values in the browser before they leave the device. Off unless
 * `VITE_META_ADVANCED_MATCHING=true`.
 */
export function setUserData(user: { email?: string; phone?: string }) {
	if (!ADVANCED_MATCHING || !metaPixelId) return;
	if (typeof window === "undefined" || typeof window.fbq !== "function") return;

	const data: Record<string, string> = {};
	if (user.email) data.em = user.email.trim().toLowerCase();
	if (user.phone) {
		// Meta expects digits only, country code included.
		const digits = user.phone.replace(/\D/g, "");
		data.ph = digits.startsWith("0") ? `213${digits.slice(1)}` : digits;
	}
	if (Object.keys(data).length === 0) return;
	try {
		window.fbq("init", metaPixelId, data);
	} catch {
		// ignore
	}
}

/* -------------------------------------------------------------------------- */
/* event catalog — session & page                                             */
/* -------------------------------------------------------------------------- */

export function trackSessionStart(path: string, locale: string) {
	session.path = path;
	session.locale = locale;
	getAttribution();
	emit("session_start", {
		meta: "SessionStart",
		withDevice: true,
		params: {
			landing_path: path,
			fbclid: getAttribution().fbclid ? "yes" : "no",
			utm_content: getAttribution().utm_content,
			utm_term: getAttribution().utm_term,
		},
	});
}

/** Client-side route change — the initial view comes from the pixel snippet. */
export function trackPageView(path: string) {
	emit("page_view_spa", { meta: "PageView", params: { page_path: path } });
}

export function trackPageRestored() {
	emit("page_restored", { meta: "PageRestored" });
}

/* -------------------------------------------------------------------------- */
/* event catalog — scroll & reading                                           */
/* -------------------------------------------------------------------------- */

export function trackScrollDepth(depth: number) {
	emit("scroll", {
		meta: `ScrollDepth${depth}`,
		params: { percent_scrolled: depth, depth_label: `${depth}%` },
	});
}

export function trackFirstScroll(direction: "down" | "up") {
	emit("first_scroll", { meta: "FirstScroll", params: { direction } });
}

export function trackScrollBack(from: number, to: number) {
	emit("scroll_back", {
		params: { from_depth: from, to_depth: to },
	});
}

export function trackSectionView(section: string, index: number) {
	emit("section_view", {
		meta: "ViewSection",
		params: {
			section_name: section,
			section_index: index,
			seconds_to_view: secondsOnPage(),
		},
	});
}

export function trackSectionDwell(section: string, seconds: number) {
	emit("section_dwell", {
		params: { section_name: section, dwell_seconds: seconds },
	});
}

/**
 * Meta's quality signal that the visitor actually consumed the offer. Several
 * triggers can claim it (reaching the course section, half the page read) —
 * only the first one reports, so the event stays a clean per-visit signal.
 */
export function trackViewContent(section: string) {
	once("view_content", () => {
		emit("view_content", {
			meta: "ViewContent",
			params: {
				content_name: "دورة إنشاء المؤسسات",
				content_category: "course",
				content_type: "product",
				trigger_section: section,
			},
		});
	});
}

export function trackTimeMilestone(seconds: number) {
	emit("time_on_page", {
		meta: `TimeOnPage${seconds}s`,
		params: { milestone_seconds: seconds },
	});
}

/* -------------------------------------------------------------------------- */
/* event catalog — clicks                                                     */
/* -------------------------------------------------------------------------- */

export interface ClickInfo {
	id: string;
	type: string;
	label?: string;
	section?: string;
	href?: string;
	position?: string;
}

export function trackCtaClick(info: ClickInfo) {
	session.ctaClicks += 1;
	emit("cta_click", {
		meta: "ClickCTA",
		params: {
			cta_id: info.id,
			cta_type: info.type,
			cta_label: info.label,
			section: info.section,
			link_url: info.href,
			position: info.position,
			cta_order: session.ctaClicks,
		},
	});
}

/** Any click that scrolls the visitor to the registration form. */
export function trackRegisterIntent(info: ClickInfo) {
	emit("register_intent", {
		meta: "RegisterIntent",
		params: {
			cta_id: info.id,
			section: info.section,
			cta_label: info.label,
		},
	});
}

export function trackNavClick(target: string, label?: string) {
	emit("nav_click", {
		meta: "ClickNav",
		params: { nav_target: target, nav_label: label },
	});
}

export function trackDownload(file: string, fileLocale: string) {
	emit("file_download", {
		meta: "DownloadBrochure",
		params: {
			file_name: file,
			file_extension: "pdf",
			file_locale: fileLocale,
			link_url: file,
		},
	});
}

export function trackContact(channel: string, position?: string) {
	emit("contact", {
		meta: "Contact",
		params: { contact_channel: channel, position },
	});
}

export function trackSchedule(source: string) {
	emit("schedule_click", { meta: "Schedule", params: { cta_id: source } });
}

export function trackOutbound(href: string, label?: string) {
	emit("outbound_click", {
		meta: "OutboundClick",
		params: { link_url: href, link_label: label },
	});
}

export function trackAccordion(group: string, index: number, question: string) {
	emit(group === "faq" ? "faq_open" : "accordion_open", {
		meta: group === "faq" ? "FAQOpen" : "AccordionOpen",
		params: { group, item_index: index, item_label: question },
	});
}

export function trackLanguageSwitch(from: string, to: string) {
	emit("language_switch", {
		meta: "LanguageSwitch",
		params: { from_locale: from, to_locale: to },
	});
}

export function trackFabShown(fab: string) {
	emit("fab_shown", { params: { fab_id: fab } });
}

/* -------------------------------------------------------------------------- */
/* event catalog — the form funnel                                            */
/* -------------------------------------------------------------------------- */

/** The form scrolled into view — the denominator for "form started". */
export function trackFormView(id: string) {
	emit("form_view", {
		meta: "FormView",
		params: { form_id: id, seconds_to_view: secondsOnPage() },
	});
}

export function startForm(id: string) {
	if (session.form?.started && session.form.id === id) return;
	session.form = {
		id,
		started: true,
		startedAt: now(),
		touched: [],
		filled: [],
		lastField: "",
		errors: 0,
		attempts: 0,
		submitted: false,
	};
	// Meta's closest standard step before a conversion — makes the funnel
	// (PageView → InitiateCheckout → Lead) readable straight from Events Manager.
	emit("form_start", {
		meta: "InitiateCheckout",
		params: { form_id: id, content_category: "lead_form" },
	});
}

export function trackFieldFocus(field: string) {
	const form = session.form;
	if (!form) return;
	form.lastField = field;
	if (form.touched.includes(field)) return;
	form.touched.push(field);
	emit("form_field_focus", {
		params: {
			form_id: form.id,
			field_name: field,
			field_order: form.touched.length,
		},
	});
}

export function trackFieldComplete(field: string, filled: boolean) {
	const form = session.form;
	if (!form) return;
	if (filled) {
		if (form.filled.includes(field)) return;
		form.filled.push(field);
		emit("form_field_complete", {
			meta: "FormFieldComplete",
			params: {
				form_id: form.id,
				field_name: field,
				fields_filled: form.filled.length,
				seconds_since_start: Math.round((now() - form.startedAt) / 1000),
			},
		});
		return;
	}
	// Focused, then left empty — the classic drop-off signal.
	emit("form_field_skipped", {
		params: { form_id: form.id, field_name: field },
	});
}

export function trackFormValidationError(field: string, message: string) {
	const form = session.form;
	if (form) form.errors += 1;
	emit("form_error", {
		meta: "FormValidationError",
		params: {
			form_id: form?.id,
			field_name: field,
			error_message: message,
			error_count: form?.errors,
		},
	});
}

export function trackFormSubmitAttempt(id: string) {
	const form = session.form;
	if (form) form.attempts += 1;
	emit("form_submit", {
		meta: "FormSubmit",
		params: {
			form_id: id,
			attempt: form?.attempts ?? 1,
			fields_filled: form?.filled.length,
			seconds_to_submit: form ? Math.round((now() - form.startedAt) / 1000) : 0,
		},
	});
}

export function trackFormFailure(id: string, reason: string) {
	emit("form_submit_failed", {
		meta: "FormFailed",
		params: { form_id: id, error_message: reason },
	});
}

/**
 * The conversion. Fires Meta `Lead` **and** `CompleteRegistration` (Meta's two
 * optimisable lead events) plus GA4 `generate_lead`.
 */
export function trackLeadConversion(
	details: {
		formId: string;
		wilaya?: string;
		jobStatus?: string;
		email?: string;
		phone?: string;
	} = { formId: "lead-form" },
) {
	const form = session.form;
	session.lead = true;
	if (form) form.submitted = true;

	setUserData({ email: details.email, phone: details.phone });

	const params: Params = {
		form_id: details.formId,
		content_name: "دورة إنشاء المؤسسات",
		content_category: "course_registration",
		wilaya: details.wilaya,
		job_status: details.jobStatus,
		seconds_to_convert: secondsOnPage(),
		fields_filled: form?.filled.length,
		form_attempts: form?.attempts,
		form_errors: form?.errors,
		currency: LEAD_VALUE ? "DZD" : undefined,
		value: LEAD_VALUE,
	};

	emit("generate_lead", { meta: "Lead", params });
	emit("complete_registration", { meta: "CompleteRegistration", params });
}

export function trackFormAbandon(reason: string) {
	const form = session.form;
	if (!form || !form.started || form.submitted) return;
	once("form_abandon", () => {
		emit("form_abandon", {
			meta: "FormAbandon",
			params: {
				form_id: form.id,
				reason,
				last_field: form.lastField,
				fields_touched: form.touched.length,
				fields_filled: form.filled.length,
				form_errors: form.errors,
				form_attempts: form.attempts,
				seconds_in_form: Math.round((now() - form.startedAt) / 1000),
			},
		});
	});
}

/* -------------------------------------------------------------------------- */
/* event catalog — leaving, friction, errors                                  */
/* -------------------------------------------------------------------------- */

export function trackExitIntent() {
	emit("exit_intent", {
		meta: "ExitIntent",
		params: {
			deepest_section: session.deepestSection,
			form_started: session.form?.started ?? false,
			has_lead: session.lead,
		},
	});
}

export function trackTabHidden() {
	session.hiddenCount += 1;
	emit("tab_hidden", {
		params: { hidden_count: session.hiddenCount },
	});
}

export function trackTabReturn(awaySeconds: number) {
	emit("tab_return", {
		params: { away_seconds: awaySeconds, return_count: session.hiddenCount },
	});
}

/** Page open, visible, but untouched for a long stretch. */
export function trackIdle(seconds: number) {
	emit("user_idle", {
		params: { idle_seconds: seconds, funnel_stage: funnelStage() },
	});
}

export function trackRageClick(target: string, count: number) {
	emit("rage_click", {
		meta: "RageClick",
		params: { target_label: target, click_count: count },
	});
}

export function trackDeadClick(target: string) {
	emit("dead_click", { params: { target_label: target } });
}

export function trackCopy(length: number, snippet: string) {
	emit("copy_text", {
		params: { copied_length: length, copied_snippet: snippet },
	});
}

export function trackPrint() {
	emit("print_page");
}

export function trackClientError(message: string, source: string) {
	emit("client_error", {
		params: { error_message: message, error_source: source },
	});
}

/** How far down the funnel the visitor got before leaving. */
function funnelStage(): string {
	if (session.lead) return "converted";
	if (session.form?.submitted) return "submitted";
	if (session.form?.started) return "form_started";
	if (session.ctaClicks > 0) return "cta_clicked";
	if (session.maxScroll >= 75) return "read_full";
	if (session.maxScroll >= 25) return "scrolled";
	if (session.interactions > 0) return "interacted";
	return "landed";
}

/**
 * Terminal event for the page view. Fired on the first `visibilitychange →
 * hidden` (the only reliably delivered signal on mobile) and on `pagehide`.
 */
export function trackPageLeave(reason: string) {
	if (session.leaveSent) return;
	session.leaveSent = true;

	const stage = funnelStage();
	const bounced =
		!session.lead &&
		session.interactions === 0 &&
		session.maxScroll < 25 &&
		engagedSeconds() < 10;

	emit("page_leave", {
		meta: "PageLeave",
		params: {
			leave_reason: reason,
			leave_index: session.hiddenCount,
			funnel_stage: stage,
			deepest_section: session.deepestSection,
			sections_list: session.sections.join(">").slice(0, 100),
			max_scroll: session.maxScroll,
			total_clicks: session.clicks,
			cta_clicks: session.ctaClicks,
			tab_hides: session.hiddenCount,
			converted: session.lead,
			bounced,
		},
	});

	// A dedicated abandonment event so it can be used as a Meta custom audience
	// ("everyone who read the offer and left without registering"). Once per
	// page view even if the visitor hides and returns to the tab several times.
	if (!session.lead) {
		once("abandonment", () => {
			emit("abandonment", {
				meta: "Abandonment",
				params: {
					funnel_stage: stage,
					max_scroll: session.maxScroll,
					form_started: session.form?.started ?? false,
					fields_filled: session.form?.filled.length ?? 0,
					engaged_seconds: engagedSeconds(),
				},
			});
		});
	}

	if (bounced) {
		once("bounce", () => {
			emit("bounce", {
				meta: "Bounce",
				params: { max_scroll: session.maxScroll },
			});
		});
	}

	trackFormAbandon(reason);
}
